import styled, { keyframes } from 'styled-components';

const COLORS = {
  danger: '#e02e2e',
  dangerBg: '#fdecec',
  success: '#1f9d57',
  successBg: '#e6f6ed',
  warning: '#d98a00',
  warningBg: '#fdf3e0',
  primary: '#2b59c3',
  text: '#333',
  muted: '#888',
  border: '#e2e2e2',
};

export const AlertBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${COLORS.dangerBg};
  border: 1px solid ${COLORS.danger};
  border-radius: 8px;
  padding: 14px 16px;
  margin: 12px 0;

  svg {
    color: ${COLORS.danger};
    flex-shrink: 0;
  }
`;

export const AlertBannerSuccess = styled(AlertBanner)`
  && {
    background-color: #e8f5e9;
    border-color: #4caf50;
    color: #1b5e20;
  }

  && strong {
    color: #1b5e20;
  }

  && span {
    color: #2e7d32;
  }

  && > svg {
    color: #4caf50;
  }
`;

export const AlertContent = styled.div`
  flex: 1;

  strong {
    display: block;
    color: ${COLORS.danger};
    font-size: 14px;
    margin-bottom: 2px;
  }

  span {
    font-size: 13px;
    color: ${COLORS.text};
  }
`;

export const AlertButton = styled.button`
  background: ${COLORS.danger};
  color: #fff;
  border: 0;
  border-radius: 6px;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  transition: filter 0.2s;

  &:hover {
    filter: brightness(0.92);
  }
`;

export const AlertButtonSuccess = styled(AlertButton)`
  && {
    background-color: #4caf50;
    border-color: #4caf50;
    color: #ffffff;
  }

  &&:hover,
  &&:focus {
    background-color: #388e3c;
    border-color: #388e3c;
    color: #ffffff;
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

export const ModalBox = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 440px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid ${COLORS.border};

  h2 {
    font-size: 16px;
    color: ${COLORS.text};
    margin: 0;
  }

  button {
    background: none;
    border: 0;
    cursor: pointer;
    color: ${COLORS.muted};
    display: flex;
  }
`;

export const ModalBody = styled.div`
  padding: 20px;
`;

export const ModalFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${COLORS.border};
  display: flex;
  gap: 10px;
`;

export const CourierCard = styled.div`
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 18px;
  display: flex;
  gap: 14px;
  align-items: center;
`;

export const CourierPhoto = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f0f0f0 url(${props => props.src || ''}) center/cover no-repeat;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.muted};
`;

export const CourierInfo = styled.div`
  flex: 1;

  .name {
    font-size: 16px;
    font-weight: 600;
    color: ${COLORS.text};
  }

  .detail {
    font-size: 13px;
    color: ${COLORS.muted};
    margin-top: 2px;
  }

  .plate {
    display: inline-block;
    margin-top: 6px;
    font-weight: 700;
    letter-spacing: 1px;
    background: #f5f5f5;
    border-radius: 4px;
    padding: 3px 8px;
    font-size: 14px;
  }
`;

export const Field = styled.div`
  margin-bottom: 14px;

  label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: ${COLORS.text};
    margin-bottom: 6px;
  }

  input,
  textarea {
    width: 100%;
    border: 1px solid ${COLORS.border};
    border-radius: 6px;
    padding: 10px 12px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: ${COLORS.primary};
    }
  }

  textarea {
    resize: vertical;
    min-height: 80px;
  }
`;

export const Button = styled.button`
  flex: 1;
  border: 0;
  border-radius: 8px;
  padding: 12px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: filter 0.2s, opacity 0.2s;

  background: ${props => {
    if (props.variant === 'success') return COLORS.success;
    if (props.variant === 'danger') return COLORS.danger;
    if (props.variant === 'ghost') return '#f0f0f0';
    return COLORS.primary;
  }};
  color: ${props => (props.variant === 'ghost' ? COLORS.text : '#fff')};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    filter: brightness(0.93);
  }
`;

export const AttemptsBadge = styled.div`
  font-size: 13px;
  color: ${props => (props.danger ? COLORS.danger : COLORS.muted)};
  text-align: center;
  margin-top: 10px;
`;

export const CodeBox = styled.div`
  text-align: center;
  padding: 12px 0;

  .label {
    font-size: 13px;
    color: ${COLORS.muted};
    margin-bottom: 12px;
  }

  .pin {
    font-size: 40px;
    font-weight: 700;
    letter-spacing: 8px;
    color: ${COLORS.text};
  }

  .hint {
    font-size: 13px;
    color: ${COLORS.muted};
    margin-top: 14px;
  }
`;

const pulse = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
`;

export const WaitingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  color: ${COLORS.primary};
  font-size: 14px;
  font-weight: 600;
  animation: ${pulse} 1.6s ease-in-out infinite;
`;

export const StatusMessage = styled.div`
  text-align: center;
  padding: 20px 0;

  svg {
    color: ${props =>
      props.variant === 'success' ? COLORS.success : COLORS.warning};
    margin-bottom: 12px;
  }

  .title {
    font-size: 18px;
    font-weight: 600;
    color: ${COLORS.text};
  }

  .subtitle {
    font-size: 14px;
    color: ${COLORS.muted};
    margin-top: 6px;
  }
`;
